import { useRef } from 'react';
import { Trans } from '@lingui/react/macro';
import { Plus, Cpu, CornerDownRight, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@/common/components/ui/button';
import { useSidebarManager } from '@/common/components/sidebar-manager/hooks/useSidebarManager';
import { deviceQueries, deviceQueryKeys } from '@/features/devices/queries';
import { DeviceService } from '@/features/devices/services/DeviceService';
import type { Device, DeviceType } from '@/features/devices/services/schemas';
import { AddDeviceSidebar } from './AddDeviceSidebar';

interface CreateSupplyPointStep3Props {
  supplyPointId: number;
  onNext: () => void;
}

export function CreateSupplyPointStep3({ supplyPointId, onNext }: CreateSupplyPointStep3Props) {
  const { showSidebar } = useSidebarManager();
  const queryClient = useQueryClient();
  const sidebarKeyRef = useRef(0);

  // Non-suspending so invalidation after device creation updates in-place
  const { data: devices = [] } = useQuery(deviceQueries.listByEnergySystem(supplyPointId));
  const { data: deviceTypes } = useSuspenseQuery(deviceQueries.types());

  const deleteMutation = useMutation({
    mutationFn: (deviceId: number) => DeviceService.delete(deviceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all }),
  });

  const openAddDevice = (parentId?: number) => {
    // Increment key to force remount of AddDeviceSidebar so its internal state resets
    const key = ++sidebarKeyRef.current;
    showSidebar(
      <AddDeviceSidebar
        key={key}
        supplyPointId={supplyPointId}
        devices={devices}
        deviceTypes={deviceTypes}
        preselectedParentId={parentId ?? null}
      />
    );
  };

  const topLevel = devices.filter((d) => d.parent === null);
  const childrenOf = (parentId: number) => devices.filter((d) => d.parent === parentId);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="text-base font-semibold">
          <Trans>Add devices</Trans>
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          <Trans>
            Add physical devices installed at this supply point. Devices can be added at any time in
            Device management.
          </Trans>
        </p>
      </div>

      <div className="px-6 py-6 space-y-4">
        {devices.length > 0 ? (
          <DeviceTree
            topLevel={topLevel}
            childrenOf={childrenOf}
            deviceTypes={deviceTypes}
            onAddChild={openAddDevice}
            onDelete={(id) => deleteMutation.mutate(id)}
            deleteIsPending={deleteMutation.isPending}
          />
        ) : (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-10 gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-muted">
              <Cpu size={18} className="text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                <Trans>No devices yet</Trans>
              </p>
              <p className="text-xs text-muted-foreground/70 mt-0.5">
                <Trans>Add your first device using the button below</Trans>
              </p>
            </div>
          </div>
        )}

        <Button variant="outline" className="gap-2" onClick={() => openAddDevice()}>
          <Plus size={15} />
          <Trans>Add device</Trans>
        </Button>
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
        <span className="text-xs text-muted-foreground">
          <Trans>Step 3 of 4 · Optional</Trans>
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onNext}>
            <Trans>Skip</Trans>
          </Button>
          <Button onClick={onNext}>
            <Trans>Continue</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Device tree ──────────────────────────────────────────────────────────────

interface DeviceTreeProps {
  topLevel: Device[];
  childrenOf: (parentId: number) => Device[];
  deviceTypes: DeviceType[];
  onAddChild: (parentId: number) => void;
  onDelete: (deviceId: number) => void;
  deleteIsPending: boolean;
}

function DeviceTree({
  topLevel,
  childrenOf,
  deviceTypes,
  onAddChild,
  onDelete,
  deleteIsPending,
}: DeviceTreeProps) {
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      {/* Single group header */}
      <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b border-border">
        <Cpu size={12} className="text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          <Trans>Devices</Trans>
        </span>
      </div>

      {topLevel.map((device, di) => {
        const children = childrenOf(device.id);
        const isLast = di === topLevel.length - 1;
        const showBottomBorder = !isLast || children.length > 0;
        const deviceType = deviceTypes.find((t) => t.id === device.deviceType);
        const typeName = deviceType?.name ?? '';
        const groupId = deviceType?.deviceGroup.id ?? null;
        const hasChildTypes =
          groupId !== null && deviceTypes.some((t) => t.deviceGroup.parent === groupId);

        return (
          <div key={device.id}>
            {/* Parent device row */}
            <div
              className={`flex items-center gap-3 px-4 py-3 bg-card${showBottomBorder ? ' border-b border-border' : ''}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground">{device.name}</span>
                  {typeName && <span className="text-xs text-muted-foreground">{typeName}</span>}
                  {hasChildTypes && (
                    <button
                      onClick={() => onAddChild(device.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-colors"
                    >
                      <Plus size={10} />
                      <Trans>Device</Trans>
                    </button>
                  )}
                </div>
              </div>
              <SavedBadge />
              <button
                onClick={() => onDelete(device.id)}
                disabled={deleteIsPending}
                className="rounded-lg p-1.5 text-muted-foreground/40 hover:bg-destructive/10 hover:text-destructive transition-colors"
              >
                <Trash2 size={13} />
              </button>
            </div>

            {/* Child device rows — indented */}
            {children.map((child, ci) => {
              const isLastChild = ci === children.length - 1;
              const isAbsoluteLast = isLastChild && isLast;
              const childTypeName = deviceTypes.find((t) => t.id === child.deviceType)?.name ?? '';
              return (
                <div
                  key={child.id}
                  className={`flex items-center gap-2 pl-8 pr-4 py-2.5 bg-card${!isAbsoluteLast ? ' border-b border-border' : ''}`}
                >
                  <CornerDownRight size={12} className="text-muted-foreground/40 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-foreground">{child.name}</span>
                    {childTypeName && (
                      <span className="ml-1.5 text-xs text-muted-foreground">{childTypeName}</span>
                    )}
                  </div>
                  <SavedBadge />
                  <button
                    onClick={() => onDelete(child.id)}
                    disabled={deleteIsPending}
                    className="rounded-lg p-1.5 text-muted-foreground/40 hover:bg-destructive/10 hover:text-destructive transition-colors shrink-0"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function SavedBadge() {
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <div className="size-1.5 rounded-full bg-emerald-400" />
      <span className="text-xs text-emerald-600">
        <Trans>Saved</Trans>
      </span>
    </div>
  );
}
